/** Unified list of all models (active, inactive, prospect, competitor, other)
 * with search + status filter + "new model" creation modal.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MagnifyingGlass,
  Plus,
  UserCircle,
  ArrowRight,
  Trash,
} from "@phosphor-icons/react";
import { modelsAdminApi, MODEL_STATUSES } from "../../api/models";
import PortalPageHeader from "../../components/portal/PortalPageHeader";
import Modal from "../../components/ui-shared/Modal";

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700",
  inactive: "bg-slate-100 text-slate-500",
  prospect: "bg-blue-50 text-blue-700",
  external: "bg-amber-50 text-amber-700",
  other: "bg-violet-50 text-violet-700",
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  return (
    <span
      data-testid={`model-status-badge-${status}`}
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
        STATUS_STYLES[status] || "bg-slate-100 text-slate-500"
      }`}
    >
      {t(`models.status.${status}`, { defaultValue: status })}
    </span>
  );
}

function NewModelForm({ onCreated, onCancel }) {
  const { t } = useTranslation();
  const [data, setData] = useState({
    status: "prospect",
    first_name: "",
    last_name: "",
    email: "",
    handle: "",
    nationality: "",
    create_account: false,
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const canCreateAccount =
    data.status === "active" || data.status === "inactive";

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      if (!data.first_name.trim()) return;
      if (data.create_account && (!data.email || data.password.length < 8)) {
        setError(t("models.new.error_account_fields"));
        return;
      }
      setSaving(true);
      try {
        const payload = {
          status: data.status,
          first_name: data.first_name.trim(),
          last_name: data.last_name.trim() || "",
          create_account: !!data.create_account && canCreateAccount,
        };
        if (data.email.trim()) payload.email = data.email.trim();
        if (data.handle.trim()) payload.handle = data.handle.trim();
        if (data.nationality.trim())
          payload.nationality = data.nationality.trim();
        if (payload.create_account) payload.password = data.password;
        const created = await modelsAdminApi.create(payload);
        onCreated?.(created);
      } catch (err) {
        setError(err?.message || t("models.new.error_generic"));
      } finally {
        setSaving(false);
      }
    },
    [data, canCreateAccount, onCreated, t],
  );

  return (
    <form onSubmit={submit} className="space-y-3">
      {/* Status */}
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
          {t("models.new.status")}
        </label>
        <select
          data-testid="new-model-status"
          value={data.status}
          onChange={(e) => setField("status", e.target.value)}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white"
        >
          {MODEL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`models.status.${s}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <input
          data-testid="new-model-first-name"
          placeholder={t("models.field.first_name")}
          value={data.first_name}
          onChange={(e) => setField("first_name", e.target.value)}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
        />
        <input
          placeholder={t("models.field.last_name")}
          value={data.last_name}
          onChange={(e) => setField("last_name", e.target.value)}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
        />
      </div>
      <input
        placeholder={t("models.field.email")}
        value={data.email}
        type="email"
        onChange={(e) => setField("email", e.target.value)}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
      />
      <div className="grid grid-cols-2 gap-3">
        <input
          placeholder={t("models.field.handle")}
          value={data.handle}
          onChange={(e) => setField("handle", e.target.value)}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
        />
        <input
          placeholder={t("models.field.nationality")}
          value={data.nationality}
          onChange={(e) => setField("nationality", e.target.value)}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
        />
      </div>

      {canCreateAccount && (
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              data-testid="new-model-create-account"
              checked={data.create_account}
              onChange={(e) => setField("create_account", e.target.checked)}
            />
            {t("models.new.create_account")}
          </label>
          {data.create_account && (
            <input
              type="password"
              data-testid="new-model-password"
              placeholder={t("models.new.password_placeholder")}
              value={data.password}
              onChange={(e) => setField("password", e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
          )}
        </div>
      )}

      {error && (
        <p data-testid="new-model-error" className="text-xs text-red-600">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
        >
          {t("common.cancel")}
        </button>
        <button
          type="submit"
          data-testid="new-model-submit"
          disabled={saving || !data.first_name.trim()}
          className="rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {t("models.new.submit")}
        </button>
      </div>
    </form>
  );
}

const FILTERS = ["all", ...MODEL_STATUSES];

export default function AdminModels() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState("all");
  const [toDelete, setToDelete] = useState(null); // { id, name } or null
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await modelsAdminApi.list());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onConfirmDelete = useCallback(async () => {
    if (!toDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await modelsAdminApi.remove(toDelete.id);
      setRows((rs) => rs.filter((r) => r.id !== toDelete.id));
      setToDelete(null);
    } catch (err) {
      setDeleteError(err?.message || t("models.delete.error_generic"));
    } finally {
      setDeleting(false);
    }
  }, [toDelete, t]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (!q) return true;
      return [r.first_name, r.last_name, r.email, r.handle]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q));
    });
  }, [rows, query, filter]);

  return (
    <div data-testid="admin-models-page" className="space-y-10">
      <PortalPageHeader
        kickerKey="admin.placeholder.kicker"
        titleKey="admin.nav.models"
        subtitleKey="admin.pages.models_desc"
        right={
          <button
            type="button"
            onClick={() => setShowNew(true)}
            data-testid="admin-models-new"
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            <Plus size={14} weight="bold" />
            {t("models.new_model")}
          </button>
        }
      />

      <div className="space-y-3">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2.5">
          <MagnifyingGlass size={16} weight="regular" className="text-slate-400" />
          <input
            type="text"
            data-testid="admin-models-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("models.search_placeholder")}
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`models-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${
                filter === f
                  ? "bg-slate-900 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-slate-900"
              }`}
            >
              {t(`models.filter.${f}`, { defaultValue: f })}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">{t("common.loading")}</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center">
          {t("models.empty")}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((r) => {
            const photoUrl = r.photo_id
              ? modelsAdminApi.photoUrl(r.photo_id)
              : null;
            const to = `/admin/models/${r.id}/profile`;
            const fullName = `${r.first_name} ${r.last_name || ""}`.trim();
            return (
              <div
                key={r.id}
                className="group relative flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 hover:border-slate-900 transition-colors"
              >
                <Link
                  to={to}
                  data-testid={`model-card-${r.id}`}
                  className="absolute inset-0 rounded-2xl"
                  aria-label={fullName}
                />
                <span className="relative inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 overflow-hidden">
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <UserCircle
                      size={28}
                      weight="light"
                      className="text-slate-400"
                    />
                  )}
                </span>
                <div className="relative flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {fullName}
                    </p>
                    <StatusBadge status={r.status} />
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {r.handle ? `@${r.handle} · ` : ""}
                    {r.email || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`model-delete-${r.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDeleteError("");
                    setToDelete({ id: r.id, name: fullName });
                  }}
                  aria-label={t("models.delete.action")}
                  title={t("models.delete.action")}
                  className="relative z-10 inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  <Trash size={16} weight="regular" />
                </button>
                <ArrowRight
                  size={16}
                  weight="bold"
                  className="relative text-slate-400 group-hover:text-slate-900 group-hover:translate-x-1 transition-all"
                />
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={showNew}
        onClose={() => setShowNew(false)}
        title={t("models.new_model_title")}
      >
        <NewModelForm
          onCancel={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            load();
          }}
        />
      </Modal>

      <Modal
        open={!!toDelete}
        onClose={() => (!deleting ? setToDelete(null) : null)}
        title={t("models.delete.title")}
        testId="confirm-delete-model"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-700">
            {t("models.delete.confirm_prefix")}{" "}
            <span className="font-bold text-slate-900">
              {toDelete?.name || ""}
            </span>{" "}
            {t("models.delete.confirm_suffix")}
          </p>
          <p className="text-xs text-slate-500">
            {t("models.delete.warning")}
          </p>
          {deleteError && (
            <p
              data-testid="model-delete-error"
              className="text-xs text-red-600"
            >
              {deleteError}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setToDelete(null)}
              disabled={deleting}
              data-testid="model-delete-cancel"
              className="rounded-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-40"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={onConfirmDelete}
              disabled={deleting}
              data-testid="model-delete-confirm"
              className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40"
            >
              <Trash size={14} weight="bold" />
              {deleting
                ? t("models.delete.in_progress")
                : t("models.delete.confirm_cta")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
